import { OneWayDragger } from "./OneWayDragger";
import { trackShape } from "@/analytics/drawing-analytics";
import { TwoPointDragger } from "./TwoPointDragger";
import { DualModeDragger } from "./DualModeDragger";
import {
  DraggerType,
  type Point2D,
  type Point3D,
  type DraggerConfig,
} from "./types";
import type { DrawingModule } from "../types/DrawingWeb";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import { deleteObj } from "../core/delete";
import { openAs, getEntityHandle, getCurrentSpaceId } from "../core/oda";
import { OpenMode } from "../core/constants";
import { AddEntitiesCommand } from "../commands/AddEntitiesCommand";
import { PREVIEW_LINE_STYLE } from "./previewStyles";
import { MeasurementLabels } from "../overlay/MeasurementLabels";

/**
 * Create a 2-vertex OdDbPolyline from `from` to `to`.
 * Note: Using polyline instead of OdDbLine due to DrawingJS bugs.
 * Pure/shared: used by both the classic single-stroke engine and the
 * two-point sequential engine below.
 */
function buildLineEntity(
  viewer: DRDrawingViewer,
  from: Point3D,
  to: Point3D,
): { id: DrawingModule.OdDbObjectId } | null {
  const module = viewer.module;
  const db = viewer.database;

  if (!db) {
    return null;
  }

  const line = module.OdDbPolyline.createObject();
  line.setDatabaseDefaults(db, true);

  const startPoint = new module.OdGePoint2d(from.x, from.y);
  const endPoint = new module.OdGePoint2d(to.x, to.y);

  line.addVertexAt(0, startPoint, 0, 0, 0, 0);
  line.addVertexAt(1, endPoint, 0, 0, 0, 0);

  deleteObj(startPoint);
  deleteObj(endPoint);

  const currentSpaceId = getCurrentSpaceId(db);
  const currentSpace = openAs(
    currentSpaceId,
    module.OdDbBlockTableRecord,
    OpenMode.kForWrite,
  );
  const entityId = currentSpace.appendOdDbEntity(line);

  deleteObj(currentSpace);
  deleteObj(line);

  return { id: entityId };
}

/**
 * Classic single-stroke line: press = start, release = end.
 * Body unchanged from the original LineDragger — only the raw entity
 * geometry (buildLineEntity) was pulled out so the two-point engine below
 * can reuse it.
 */
export class LineOneWayEngine extends OneWayDragger {
  readonly type = DraggerType.Line;

  private linePreviewId: string | null = null;
  private measureLabels: MeasurementLabels | null = null;

  // ============== Preview ==============

  protected createPreview(screen: Point2D): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    this.linePreviewId = overlay.createLine(
      screen.x,
      screen.y,
      screen.x,
      screen.y,
      PREVIEW_LINE_STYLE,
    );
    if (this.linePreviewId) {
      this.addPreviewId(this.linePreviewId);
    }
  }

  protected updatePreview(screen: Point2D): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay || !this.linePreviewId || !this.startScreen) return;

    overlay.update(this.linePreviewId, {
      x1: this.startScreen.x,
      y1: this.startScreen.y,
      x2: screen.x,
      y2: screen.y,
    });

    this.updateMeasurements(
      this.startScreen,
      screen,
      this.startWorld,
      this.currentWorld,
    );
  }

  private updateMeasurements(
    startScreen: Point2D,
    screen: Point2D,
    startWorld: Point3D | null,
    currentWorld: Point3D | null,
  ): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay || !startWorld || !currentWorld) return;

    if (!this.measureLabels) {
      this.measureLabels = new MeasurementLabels(
        overlay,
        this.viewer.getBackgroundColor(),
      );
    }

    const dx = screen.x - startScreen.x;
    const dy = screen.y - startScreen.y;
    const screenLen = Math.sqrt(dx * dx + dy * dy);

    if (screenLen > 1) {
      let nx = -dy / screenLen;
      let ny = dx / screenLen;
      if (ny > 0) {
        nx = -nx;
        ny = -ny;
      }

      const midX = (startScreen.x + screen.x) / 2 + nx * 18;
      const midY = (startScreen.y + screen.y) / 2 + ny * 18;

      const wdx = currentWorld.x - startWorld.x;
      const wdy = currentWorld.y - startWorld.y;
      const worldLen = Math.sqrt(wdx * wdx + wdy * wdy);

      this.measureLabels.setLabel(
        "length",
        midX,
        midY,
        MeasurementLabels.formatDistance(worldLen),
      );
    }
  }

  // ============== Entity Creation ==============

  protected createEntity(): void {
    if (!this.startWorld || !this.currentWorld) return;

    const entityInfo = buildLineEntity(
      this.viewer,
      this.startWorld,
      this.currentWorld,
    );

    if (entityInfo) {
      this.applyNoteConfigToEntity(entityInfo.id);

      const entityIds: DrawingModule.OdDbObjectId[] = [entityInfo.id];

      const midX = (this.startWorld.x + this.currentWorld.x) / 2;
      const midY = (this.startWorld.y + this.currentWorld.y) / 2;
      const labelId = this.createLabelIfEnabled(midX, midY);
      if (labelId) entityIds.push(labelId);

      const command = new AddEntitiesCommand(this.viewer, {
        entityHandles: entityIds.map((id) => getEntityHandle(id)),
        originalLayerName: this.viewer.activeNoteConfig.props.layername ?? "0",
      });
      this.viewer.executeCommand(command);
      trackShape("line");

      const extras = labelId ? [labelId] : [];
      this.selectCreatedEntity(entityInfo.id, extras);
      for (const id of entityIds) {
        deleteObj(id);
      }
    }
  }

  protected clear(): void {
    super.clear();
    this.measureLabels?.clear();
    this.measureLabels = null;
    this.linePreviewId = null;
  }
}

/**
 * Two-point line: press-drag-release places the start point, press-drag-
 * release again places the end point and creates the entity.
 */
export class LineTwoPointEngine extends TwoPointDragger {
  readonly type = DraggerType.Line;

  private linePreviewId: string | null = null;
  private measureLabels: MeasurementLabels | null = null;

  // ============== Preview ==============

  protected createPreview(screen: Point2D): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    this.linePreviewId = overlay.createLine(
      screen.x,
      screen.y,
      screen.x,
      screen.y,
      PREVIEW_LINE_STYLE,
    );
    if (this.linePreviewId) {
      this.addPreviewId(this.linePreviewId);
    }
  }

  protected updatePreview(screen: Point2D, world: Point3D): void {
    const overlay = this.viewer.overlayManager;
    const anchorWorld = this.getSnapReferencePoint();
    if (!overlay || !this.linePreviewId || !anchorWorld) return;

    const anchorScreen = this.viewer.worldToScreen(anchorWorld);

    overlay.update(this.linePreviewId, {
      x1: anchorScreen.x,
      y1: anchorScreen.y,
      x2: screen.x,
      y2: screen.y,
    });

    this.updateMeasurements(anchorScreen, screen, anchorWorld, world);
  }

  private updateMeasurements(
    startScreen: Point2D,
    screen: Point2D,
    startWorld: Point3D,
    currentWorld: Point3D,
  ): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    if (!this.measureLabels) {
      this.measureLabels = new MeasurementLabels(
        overlay,
        this.viewer.getBackgroundColor(),
      );
    }

    const dx = screen.x - startScreen.x;
    const dy = screen.y - startScreen.y;
    const screenLen = Math.sqrt(dx * dx + dy * dy);

    if (screenLen > 1) {
      let nx = -dy / screenLen;
      let ny = dx / screenLen;
      if (ny > 0) {
        nx = -nx;
        ny = -ny;
      }

      const midX = (startScreen.x + screen.x) / 2 + nx * 18;
      const midY = (startScreen.y + screen.y) / 2 + ny * 18;

      const wdx = currentWorld.x - startWorld.x;
      const wdy = currentWorld.y - startWorld.y;
      const worldLen = Math.sqrt(wdx * wdx + wdy * wdy);

      this.measureLabels.setLabel(
        "length",
        midX,
        midY,
        MeasurementLabels.formatDistance(worldLen),
      );
    }
  }

  // ============== Entity Creation ==============

  protected createEntity(a: Point3D, b: Point3D): void {
    const entityInfo = buildLineEntity(this.viewer, a, b);

    if (entityInfo) {
      this.applyNoteConfigToEntity(entityInfo.id);

      const entityIds: DrawingModule.OdDbObjectId[] = [entityInfo.id];

      const midX = (a.x + b.x) / 2;
      const midY = (a.y + b.y) / 2;
      const labelId = this.createLabelIfEnabled(midX, midY);
      if (labelId) entityIds.push(labelId);

      const command = new AddEntitiesCommand(this.viewer, {
        entityHandles: entityIds.map((id) => getEntityHandle(id)),
        originalLayerName: this.viewer.activeNoteConfig.props.layername ?? "0",
      });
      this.viewer.executeCommand(command);
      trackShape("line");

      const extras = labelId ? [labelId] : [];
      this.selectCreatedEntity(entityInfo.id, extras);
      for (const id of entityIds) {
        deleteObj(id);
      }
    }
  }

  protected override clearLiveMeasurements(): void {
    this.measureLabels?.clear();
    this.measureLabels = null;
  }

  protected clear(): void {
    super.clear();
    this.measureLabels?.clear();
    this.measureLabels = null;
    this.linePreviewId = null;
  }
}

/**
 * Registered dragger for DraggerType.Line. Routes to either the classic
 * single-stroke engine or the two-point sequential engine, toggled via
 * setTwoPointMode() (see DraggerMenuState.sequentialPointMode).
 */
export class LineDragger extends DualModeDragger<
  LineOneWayEngine,
  LineTwoPointEngine
> {
  readonly type = DraggerType.Line;

  constructor(viewer: DRDrawingViewer, config: DraggerConfig = {}) {
    super(
      viewer,
      config,
      new LineOneWayEngine(viewer, config),
      new LineTwoPointEngine(viewer, config),
    );
  }
}
